import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = () => axios.create({
  baseURL: '/api',
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

/**
 * WorkerOnboarding3Step
 * 
 * Fast onboarding for new workers:
 * 1. Quick Start (phone, bank details, availability)
 * 2. Profile Strength (photo, bio, certifications)
 * 3. First Shift (browse & apply to first shift)
 * 
 * Shows only if user has completed_onboarding_3step = false
 * Dismissible but tracks completion
 */
export default function WorkerOnboarding3Step({ userId, onComplete }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    phone: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_bsb: '',
    availability_set: false,
    profile_photo_url: '',
    profile_bio: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);

  // Fetch current user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const res = await api().get('/users/profile');
        if (res.data.completed_onboarding_3step) {
          setDismissed(true);
          if (onComplete) onComplete();
        } else {
          setFormData(prev => ({
            ...prev,
            phone: res.data.phone || '',
            bank_account_name: res.data.bank_account_name || '',
            bank_account_number: res.data.bank_account_number || '',
            bank_bsb: res.data.bank_bsb || '',
            profile_photo_url: res.data.profile_photo_url || '',
            profile_bio: res.data.profile_bio || '',
          }));
        }
      } catch (err) {
        console.error('Failed to fetch user data:', err);
      }
    };
    fetchUserData();
  }, [onComplete]);

  const updateCompletionPercentage = () => {
    let completed = 0;
    if (formData.phone) completed++;
    if (formData.bank_account_name && formData.bank_account_number && formData.bank_bsb) completed++;
    if (formData.availability_set) completed++;
    if (formData.profile_photo_url) completed++;
    if (formData.profile_bio) completed++;
    setCompletionPercentage(Math.round((completed / 5) * 100));
  };

  useEffect(() => {
    updateCompletionPercentage();
  }, [formData]);

  const handleStepComplete = async (nextStep) => {
    setLoading(true);
    setError('');
    try {
      // Update user profile with current form data
      await api().patch('/users/profile', {
        phone: formData.phone,
        bank_account_name: formData.bank_account_name,
        bank_account_number: formData.bank_account_number,
        bank_bsb: formData.bank_bsb,
        profile_photo_url: formData.profile_photo_url,
        profile_bio: formData.profile_bio,
      });

      if (nextStep > 3) {
        // Mark onboarding as complete
        await api().patch('/users/profile', { completed_onboarding_3step: true });
        setDismissed(true);
        if (onComplete) onComplete();
      } else {
        setStep(nextStep);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save progress');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      await api().patch('/users/profile', { completed_onboarding_3step: true });
      setDismissed(true);
      if (onComplete) onComplete();
    } catch (err) {
      console.error('Failed to skip onboarding:', err);
    }
  };

  if (dismissed) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 animate-fade-in">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl animate-slide-up">
        
        {/* Progress bar */}
        <div className="h-1 bg-neutral-200 w-full rounded-t-2xl overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-300"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>

        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Step {step} of 3</p>
            <h3 className="font-brand text-lg font-bold text-neutral-900 mt-1">
              {step === 1 && "Get Paid Fast"}
              {step === 2 && "Build Your Profile"}
              {step === 3 && "Apply to Your First Shift"}
            </h3>
          </div>
          <button
            onClick={handleSkip}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
            title="Skip onboarding"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-4">
          {error && (
            <div className="text-xs text-rose-600 bg-rose-50 px-3 py-2 rounded-lg font-medium">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4 animate-fade-in">
              <p className="text-sm text-neutral-600">
                Add your payment details so we can pay you on the same day you work.
              </p>

              {/* Phone */}
              <div>
                <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">Phone number</label>
                <input
                  type="tel"
                  placeholder="0412 345 678"
                  value={formData.phone}
                  onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="input w-full px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Bank details */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-neutral-600">Bank account</p>
                <input
                  type="text"
                  placeholder="Account name"
                  value={formData.bank_account_name}
                  onChange={e => setFormData(prev => ({ ...prev, bank_account_name: e.target.value }))}
                  className="input w-full px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    placeholder="BSB"
                    maxLength="6"
                    value={formData.bank_bsb}
                    onChange={e => setFormData(prev => ({ ...prev, bank_bsb: e.target.value }))}
                    className="input px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <input
                    type="text"
                    placeholder="Account #"
                    value={formData.bank_account_number}
                    onChange={e => setFormData(prev => ({ ...prev, bank_account_number: e.target.value }))}
                    className="input col-span-2 px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <p className="text-[11px] text-neutral-500">
                ✓ Secure & encrypted. Your bank details are only used for payouts.
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 animate-fade-in">
              <p className="text-sm text-neutral-600">
                A complete profile gets you hired 3× faster. Employers want to see who they're hiring.
              </p>

              {/* Profile photo (simplified: just URL for now) */}
              <div>
                <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">Profile photo</label>
                <div className="flex items-center gap-3">
                  {formData.profile_photo_url && (
                    <img src={formData.profile_photo_url} alt="Profile" className="w-12 h-12 rounded-full object-cover border border-neutral-200" />
                  )}
                  <button
                    type="button"
                    className="flex-1 px-4 py-3 text-sm border border-dashed border-neutral-300 rounded-lg hover:border-emerald-500 text-neutral-600 hover:text-emerald-600 transition-all"
                  >
                    📸 Upload photo
                  </button>
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">About you (optional)</label>
                <textarea
                  placeholder="E.g. 'Reliable, hard-working, great with customers'"
                  value={formData.profile_bio}
                  onChange={e => setFormData(prev => ({ ...prev, profile_bio: e.target.value }))}
                  className="input w-full px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  rows={3}
                />
                <p className="text-[11px] text-neutral-500 mt-1">Max 100 chars</p>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 animate-fade-in">
              <p className="text-sm text-neutral-600">
                Ready? Browse shifts and apply to your first one. It takes 30 seconds and pays the same day.
              </p>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
                <div className="flex gap-3">
                  <div className="text-2xl">✓</div>
                  <div className="text-sm">
                    <p className="font-semibold text-emerald-900">Payment details saved</p>
                    <p className="text-emerald-700 text-xs mt-0.5">You'll get paid same-day after your shift</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="flex gap-3">
                  <div className="text-2xl">🔒</div>
                  <div className="text-sm">
                    <p className="font-semibold text-blue-900">Fair Work protected</p>
                    <p className="text-blue-700 text-xs mt-0.5">All shifts meet award wages & legal requirements</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                <div className="flex gap-3">
                  <div className="text-2xl">👥</div>
                  <div className="text-sm">
                    <p className="font-semibold text-purple-900">Trusted by 100s of employers</p>
                    <p className="text-purple-700 text-xs mt-0.5">Browse verified shifts from real companies</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-100 flex gap-3">
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-all"
              disabled={loading}
            >
              Back
            </button>
          )}
          <button
            onClick={() => handleStepComplete(step + 1)}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-all disabled:opacity-50"
          >
            {loading ? 'Saving...' : step === 3 ? 'Start Earning' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
